// ============================================================================
// ARTICLE CONTENT — This is where you write your articles.
// Every article you write on your own portfolio site lives in this file.
// ============================================================================
//
// TO ADD A NEW ARTICLE:
//   1. Copy an existing article block below (from `{` to `},`)
//   2. Paste it at the TOP of the array (right after the opening `[`)
//   3. Change the fields — slug, title, date, subtitle, tags, readingTime, body
//   4. Write your article in the `body` field using simple Markdown (see guide)
//   5. Save. Run `yarn build`. Push to GitHub. Done.
//
// The `slug` becomes the URL of your article. Example:
//   slug: 'chaos-engineering-at-scale'   ->   veereshwali.in/#/writing/chaos-engineering-at-scale
//
// The `body` uses Markdown. A cheat-sheet:
//   # Heading 1        (used automatically for the article title, don't repeat)
//   ## Heading 2       (main section heading)
//   ### Heading 3      (sub-section)
//   **bold text**
//   *italic text*
//   [link text](https://example.com)
//   - a bullet point
//   1. a numbered list item
//   > A blockquote for pull-quotes and callouts.
//   `inline code`
//   ```
//   a code block
//   ```
//   ---   (a horizontal separator line)
//
// Leave a BLANK LINE between paragraphs. That's how Markdown knows a paragraph ended.
// ============================================================================

export const articleContent = [
  {

    slug: 'chaos-engineering-in-practice',
    title: 'Chaos Engineering in Practice',
    subtitle: "A practitioner's guide for SRE teams",
    date: '2026-05-20',
    readingTime: 8,
    tags: ['SRE', 'Reliability', 'Chaos Engineering'],
    body: `
Most teams treat reliability as something you protect *after* an outage. The real shift happens when you start 
injecting failure on purpose - and measuring how the system, and the humans behind it, respond.

This is a short field guide to running your first Chaos Engineering programme inside a large, regulated enterprise. 
It is based on lessons from real Game Days across banking platforms with strict change-management and audit boundaries.

## Start with a hypothesis, not a tool

Too many teams start by installing Gremlin or AWS FIS on day one. Skip that. Start with a whiteboard question:

> *If our primary Kafka broker disappears at 09:15 IST on a business day, what happens to downstream sanctions 
screening?*

Once you can articulate the hypothesis, you have already found half the value - you have discovered which parts of 
your architecture the team **thinks** they understand, but actually do not.

## Blast radius: small, then smaller

Your first experiment should feel almost boring. A single pod restart in a non-production cluster. Nothing more.

Then, and only then, do you grow:

1. Non-production, single component, planned window
2. Non-production, dependent components, planned window
3. Pre-production, single component, business hours
4. Production, single component, off-hours, opt-in
5. Production, business hours, with client-facing traffic

Moving between these levels is not a technical decision. It is a governance one.

## The Game Day is where SRE culture is born

A Game Day is a scheduled, cross-functional session where you inject the failure with the whole room watching - 
engineering, product, risk, ops, and often the business owner.

The purpose is never just to break something. It is to observe:

- Did our alerts fire?
- Did the right people get paged?
- Did the runbook match reality?
- Did the incident channel reach quorum in under five minutes?
- Was the customer impact what we predicted?

Every Game Day should end with two artefacts: a **blameless postmortem** and a list of concrete action items with 
named owners.

## What to measure

Do not measure how many experiments you ran. Measure:

- **MTTD** - how long until the platform noticed
- **MTTR** - how long until service restored
- **Escalation quality** - did the first responder resolve it, or did we page five people
- **Runbook accuracy** - how many steps in the runbook were wrong or missing

Over time, watch these numbers converge. When they do, your SRE function is real.

## Where most programmes stall

Three failure patterns show up repeatedly:

1. **Tool-first thinking.** The team spends four months integrating a chaos tool and never runs an experiment. 
Solution: run a Game Day with \`kubectl delete pod\` before you procure anything.
2. **No executive air cover.** A single failed experiment in production creates fear, and the programme quietly dies. 
Solution: get written sponsorship, publish the results (good and bad), and celebrate the finds.
3. **Chaos without observability.** If you cannot see the blast radius, you cannot learn from it. 
Fix logging, metrics, and traces *before* you break anything on purpose.

---

Chaos Engineering is not about breaking things. It is about building the confidence that when things break - 
and they will - your platform, your teams, and your leadership will all know exactly what to do.

Start small. Start today. Write it down.
`
  },





  {
    slug: 'welcome-to-my-writing',
    title: 'Welcome to my writing shelf',
    subtitle: 'Why I moved my writing off LinkedIn',
    date: '2026-08-02',
    readingTime: 3,
    tags: ['Meta', 'Career'],
    body: `
For the last two years I have written on LinkedIn. It is a great place to be *found* - 
but it is a terrible place to *own* your work.

Starting today, every new article will live here first, on my own domain. LinkedIn will get a cross-post 
with a link back. This lets me:

- Own the URL of every idea I publish
- Format long-form pieces the way they deserve - headings, quotes, code, structure
- Search my own archive in one place
- Keep control if any platform changes its rules tomorrow

## What you will find here

Field notes from twenty-two years of running production systems for global banks. Concretely, expect writing on:

1. **Site Reliability Engineering** - SLI/SLO frameworks, error budgets, blameless postmortems, on-call design
2. **Platform Engineering** - Kafka, Splunk, ELK/EFK at petabyte scale, cloud modernisation, Terraform
3. **Disaster Recovery & Chaos** - Game Days, RTO/RPO planning, resilience testing
4. **Applied Generative AI** - RAG in the enterprise, LLM workflows, secure production deployments
5. **Engineering leadership** - building teams, coaching, agile transformation inside GCCs

## How to read along

- **[Subscribe on LinkedIn](https://www.linkedin.com/in/veeresh-wali/)** - I will still cross-post there
- **[Follow me on GitHub](https://github.com/wali-veer)** - code and demos land there first

> The best engineering leaders remain curious, embrace new technologies, and never stop learning.

That is why this shelf exists. See you in the next post.
`
  },



  {
    slug: 'Tokens-hidden-currency',
    title: 'Tokens: The Hidden Currency of Enterprise',
    subtitle: 'A short introduction to LLM Tokens',
    date: '2026-08-08',
    readingTime: 10,
    tags: ['Token', 'LLM', 'AI'],
    body: `

#  Tokens: The Hidden Currency of Enterprise AI

##  A Personal Reflection

One of the interesting things I have noticed while exploring Large Language Models is how 
the understanding of tokens evolves over time.

When most of us first hear the word *token*, it usually appears in the context of pricing. 
A model charges a certain amount per million input tokens and a different amount per million output tokens. 
Naturally, we begin to think of tokens simply as the unit used for billing. Ofcourse, that is true - 
but it is only the tip of the iceberg.

The more I explored AI / LLMs, the more I realized that tokens influence most of the 
important engineering decision while building enterprise AI applications.

They influence how much information a model can process. They determine how much context we can send. 
They affect response latency. They influence operating cost. They impact scalability. 
They even influence the architecture of enterprise AI platforms.

Tokens are not simply a pricing metric. They are one of the fundamental building blocks of every 
interaction with a language model.

Every prompt begins as tokens. Every response ends as tokens. Every API call is measured in tokens. 
Enterprise AI platform eventually monitors tokens and pays for those tokens.

Understanding tokens, therefore, is not about understanding billing alone. It is about understanding 
how enterprise AI systems behave based on the prompt engineering, design and implementation.


---

##  Why This Chapter Matters

Tokens are not technically difficult to understand — the basic concept is remarkably simple. 
We should recognize their influence on the overall system and their influnce on the 
connecting interface business applications.

In traditional enterprise applications, engineers routinely monitor metrics such as:

- CPU utilization
- Memory consumption
- Disk I/O
- Network throughput
- Database latency

These metrics tell us how efficiently our applications are operating. Enterprise AI introduces 
another operational metric that deserves the same level of attention: **token consumption**.
Initially, token consumption appears to be a financial metric. Over time, it becomes much more than that. 
It helps answer questions such as:

- Why has application latency increased?
- Why are AI operating costs growing faster than user growth?
- Why has the context window reached its limit?
- Which prompts are inefficient? Why they generate response not as per the business requirements?
- Which model should be used for a particular workload?
- Which business unit is consuming most of the AI budget?

These questions are about prompt engineering. 
They are engineering questions. And engineering questions are what determine whether an AI 
application succeeds in production.

> Enterprise AI Engineering enables organizations to build reliable, scalable, and cost-efficient 
AI applications.

##  Humans Read Words. LLMs Read Tokens.
One of the misconceptions about Large Language Models is that they understand language 
the same way humans do. They don't. When we read the sentence:

> Our brain naturally understands words, grammar, punctuation, and meaning. The language model sees 
something entirely different.

Before the model can process the sentence, it first converts the text into smaller units called tokens. 
These tokens become the actual input to the model. The model never receives the sentence exactly 
as we typed it - it receives a sequence of tokens generated by the tokenizer.

This process happens automatically for every request. Whether we submit a prompt through ChatGPT, 
invoke an API through programming languages, or interact with an enterprise AI application, 
tokenization always happens before the model begins processing.


\`\`\`mermaid
flowchart LR
    subgraph Training ["How LLM Sees the prompt"]
        A["Raw text prompt"] --> B["Tokenizer\n(BPE / SentencePiece)"]
        B --> C["Sequence of token IDs\n[1023, 402, 8891, ...]"]
        C --> D["Embedding lookup"]
        D --> E["Transformer\n(the model itself)"]
        E --> F["Output token IDs"]
        F --> G["Detokenizer"]
        G --> H["Human-readable response"]
    end
\`\`\`

#### Figure 1 - How an LLM Sees Your Prompt. 
Before an LLM processes a prompt, it converts the input text into tokens. Notice that the tokenizer 
doesn't simply split the sentence into words. Depending on the tokenizer, punctuation marks and 
parts of words may also become separate tokens.

##  How Tokenization Actually Works
Most production tokenizers — GPT's \`tiktoken\`, Claude's tokenizer, LLaMA's SentencePiece — use 
a technique called **subword tokenization**, most commonly **Byte Pair Encoding (BPE)**.

The idea is simple once you see it:
1. Start with individual characters or bytes as the smallest units.
2. Scan a massive training corpus and find the most frequently occurring adjacent pair of units.
3. Merge that pair into a single new unit and add it to the vocabulary.
4. Repeat this process tens of thousands of times.
 
The result is a fixed vocabulary — typically 50,000 to 200,000 entries — made up of whole 
common words (\`the\`, \`is\`), frequent sub-words (\`ing\`, \`tion\`, \`pre\`), and, for rare or 
unfamiliar text, individual characters or byte fragments.

\`\`\`mermaid
flowchart LR
	subgraph Training [BPE Vocabulary Construction]
	S1["Load of raw text"] --> S2["Split into characters/bytes"]
	S2 --> S3["Count frequency of adjacent pairs"]
	S3 --> S4["Merge most frequent pair\ninto new symbol"]
	S4 --> S5{"Target vocab\nsize reached?"}
	S5 -- No --> S3
	S5 -- Yes --> S6["Final vocabulary\n(50K–200K tokens)"]
	end
\`\`\` 

**Building a Tokenizer's Vocabulary.** This is why familiar English words often map to 
a single token, while an uncommon word, a typo, a piece of code, or a non-English word 
frequently splits into several tokens. A model's fluency in a language is directly reflected 
in how efficiently that language tokenizes — this is one of the quieter reasons multilingual 
prompts can cost more than they appear to.

---

##  A Simple Example

Let's look at the following sentence:

> Sumptuous and satisfying meal at the Innovation Event, CityCenter.

To us, this appears to be a single sentence containing a handful of words. The tokenizer sees 
something different — it breaks the sentence into a sequence of tokens before sending them to 
the language model. A rough breakdown might look like this:

![OpenAI Tokenizer](/images/TokenizerTokenCount.png)

| Text fragment | Token(s) |
|---|---|
| \`Sumpt\` \`uous\` | 2 tokens (uncommon word splits) |
| \` and\` | 1 token |
| \` satisfying\` | 1 token |
| \` meal\` | 1 token |
| \` at \` \`the\` | 2 tokens |
| \` Innovation\` \`Event\` | 2 tokens |
| \`,\` | 1 token (punctuation is its own token) |
| \` City\` \`Center\` | 2 tokens (proper nouns often split) |
| \`.\` | 1 token |

A ten-word sentence becomes roughly thirteen tokens. Several observations follow from this:

1. The tokenizer does not necessarily treat every word as a single token.
2. Punctuation marks such as commas and periods may become individual tokens depending on the tokenizer.
3. The number of tokens is usually different from both the number of words and the number of 
characters - as a rough rule of thumb for English, **1 token ≈ 4 characters ≈ 0.75 words**, 
but this ratio degrades quickly for uncommon words, code, and non-English text.

This explains why we should avoid estimating AI cost based solely on word count. 
The language model never bills us for words - it bills us for tokens.

---

##  Tokens Are the Currency of Enterprise AI

In cloud engineering, we're accustomed to tracking resources such as CPU, memory, storage, and 
network utilization to optimize performance and control costs.

Enterprise AI adds a new resource to that list: **tokens**. Just as infrastructure resources require 
governance, tokens must be monitored, optimized, and managed to build cost-effective and scalable AI systems.

Every token carries three things simultaneously:
- Information   
- Processing time
- Cost

More tokens give a model more context - and usually better output. But every extra token adds cost and latency. 
Trim too aggressively, and quality drops.

That's the real prompt engineering problem: not minimizing tokens, but spending them deliberately. 
Few-shot examples are a good case - they reliably improve quality, but each one adds real token weight to a prompt.

There's no universal right size. A quick FAQ chatbot can run lean. A deep analysis task may need the extra context. 
The trade-off isn't a flaw to eliminate — it's the design decision itself.

> **From the Engineering Desk.** One of the biggest mindset shifts for engineering teams 
adopting AI is recognizing that tokens are not merely a billing unit. They are an operational 
metric. In mature AI platforms, token consumption deserves the same visibility as CPU utilization, 
memory usage, application latency, and cloud spend. Teams that measure token usage early gain much 
better control over scalability, cost, and long-term operational efficiency.

---

##  Context Windows: The Token Budget

AI models have a **context window** - a hard ceiling on the total number of tokens (input plus output) 
it can process in a single request. This is not a soft guideline; it is architectural. 
Attention mechanisms inside a transformer scale with sequence length, and the model's key-value 
cache has to hold every token of the conversation in memory for as long as generation continues.

Think of the context window as a fixed-size shared budget, not two independent budgets:

 \`\`\`mermaid

flowchart LR

subgraph CW["Context Window (e.g., 200,000 tokens)"]
A["System Prompt\n~500 tokens"] --> B["Conversation History\n~40,000 tokens"]
B --> C["Retrieved Documents\n~8,000 tokens"]
C --> D["Current User Message\n~200 tokens"]
D --> E["Remaining Budget\nfor Model's Response"]
end
\`\`\` 

**The Context Window as a Shared Budget.** Everything that goes in - system instructions, 
chat history, retrieved documents, tool definitions - competes for the same finite space as the model's 
own output. A long conversation history isn't just a latency and cost problem; past a certain point it 
can crowd out the room the model needs to generate a complete answer, or force truncation of earlier 
context altogether.

This is precisely why techniques like **conversation summarization, retrieval-augmented generation, 
and prompt caching** matter operationally, not just financially - they aren't only cost levers, 
they are what keeps an application inside its architectural limits as conversations grow.

---

##  Not All Tokens Are Equal

When developers invoke an LLM through an API, the response metadata is often overlooked. 
Most of us focus on the response generated by the model - usually our experience is on manually 
prompting an LLM. The API, however, quietly returns another piece of information that is equally 
valuable - few of them are the usage statistics.

A snippet of accessing the usage metadata though response object is shown below. 
The snippet is in Python
\`\`\`python
	.
	.
	in_tokens = response.usage.input_tokens
    out_tokens = response.usage.output_tokens
    total_tokens = response.usage.total_tokens
    response_id = response.id
    model_name = response.model

    print(f"Input token count :  {in_tokens}")
    print(f"Output token count :  {out_tokens}")
    print(f"Total token count :  {total_tokens}")
    print(f"Response ID :  {response_id}")
    print(f"Model Name :  {model_name}")
	.
	.
\`\`\`

The output of the above snippet is shown below.

\`\`\`
Input token count :  12
Output token count :  2605
Total token count :  2617
Response ID :  resp_0a132f200a9500be006a781f467d44819a8de5be1caa8ed447
Model Name :  gpt-5-nano-2025-08-07
\`\`\`

To someone building a prototype, token counts look like trivia. To an engineering team running AI at 
enterprise scale, they're operational metrics - the parameters that shape how the application actually 
gets built and run.

***Every request leaves a footprint:*** how much information went in, how much came back, what it cost, 
and how efficiently the system is operating. The response solves the user's problem. The metadata 
is what lets engineering teams improve the platform.

This information, returned with every request, provides operational insight including model used, 
input token count, output token count, total tokens consumed, completion status, and other execution details. 
It forms the foundation of cost monitoring, observability, capacity planning, and enterprise AI governance.

###  Input Tokens vs Output Tokens

Every API request consists of two distinct phases.

**Phase 1 - Reading.** The model first reads everything we send. This includes:

- System prompts
- User prompts
- Previous conversation history
- Retrieved documents (RAG)
- Function definitions
- Tool descriptions

All of these contribute to **input tokens**.

**Phase 2 - Generation.** Once the model has processed the input, it begins generating a response. 
Every word it generates also becomes tokens. These are called **output tokens**. 
Unlike input tokens, output tokens represent new content created by the model.

\`\`\`mermaid

sequenceDiagram

participant App as Application

participant API as LLM API

App->>API: System prompt + history + RAG docs + user message

Note over API: Phase 1 - Reading<br/>(Input Tokens)

API->>API: Predict next token, repeat

Note over API: Phase 2 - Generation<br/>(Output Tokens)

API-->>App: Response text + usage metadata

Note over App: input_tokens, output_tokens,<br/>total_tokens, latency

\`\`\`

**The Two-Phase Anatomy of an LLM Request.**

Think of a human conversation. Reading a two-page document usually takes less effort than 
writing a two-page report. Language models behave similarly - reading existing information 
is computationally less intensive than generating new text. This is one of the reasons 
commercial LLM providers typically price input tokens and output tokens differently.

> LLM models typically price input tokens and output tokens differently.

###  Why Are Input and Output Tokens Charged Separately?

This question is surprisingly common. At first glance, a token is simply a token - why should the 
pricing differ?

The answer lies in how language models work internally. Processing the prompt requires the model to 
understand the context in a single forward pass across the entire input. Generating a response 
requires the model to predict the next token repeatedly, one at a time, feeding each new token 
back in before it can predict the next one - until the response is complete. Each generated token 
involves this additional, sequential computation.

Consequently, generating text is generally more computationally expensive than reading existing text. 
This difference is reflected in the pricing models adopted by most commercial LLM providers. 
For example, OpenAI publishes separate pricing for input tokens, cached input tokens (where applicable), 
and output tokens.

Understanding this distinction is essential because enterprise AI costs are influenced not only 
by the size of the prompt but also by the size of the generated response. We might focus 
exclusively on optimizing prompts while overlooking the fact that verbose responses can contribute 
even more to the overall cost.

**Below shown screenshots - OpenAI Usage Dashboard.** A billing dashboard that separates token 
consumption into input tokens, output tokens, cached input tokens, and uncached input tokens. 
These distinctions become increasingly important as AI applications move from experimentation 
into production, informing cost allocation, capacity planning, prompt optimization, performance analysis, 
and enterprise chargeback.

![Token Consumption](/images/TokenCount.png)
![Usage and billing](/images/Usage-Billing.png)

###  A Note on Multimodal Tokens

Tokenization isn't limited to text. Images, audio, and videos are also converted into 
tokens before a multimodal AI model can process them.

In the case of images, the model divides the image into small patches and converts 
each patch into tokens. Consequently, a single high-resolution image can consume 
more tokens than several paragraphs of text.

As enterprises increasingly include screenshots, scanned documents, and architecture 
diagrams in AI workflows, image token consumption becomes just as important to 
monitor as text—it affects both cost and the available context window.

---

##  Controlling Input Tokens

There are several practical techniques that help reduce input token consumption.

**Remove redundant instructions.** Avoid repeating identical instructions across every request. 
Instead, use reusable system prompts or prompt templates.

**Use Retrieval-Augmented Generation (RAG).** Rather than sending an entire knowledge base to the 
model, retrieve only the documents relevant to the user's question. Smaller context, lower token usage, 
lower cost.

**Summarize long conversations.** Sending the complete conversation history with every request 
eventually becomes expensive. Many enterprise AI systems periodically summarize older conversations 
before continuing, preserving context while significantly reducing token consumption.

**Cache reusable prompts.** Static instructions rarely change. Modern AI platforms increasingly 
support prompt caching - the model provider stores the processed representation of a repeated prefix 
(like a large system prompt) so subsequent requests don't pay full price to re-process it, 
reducing both latency and cost for repeated requests.

\`\`\`mermaid
flowchart LR
R1["Request 1:\nSystem prompt (2000 tok) + query"] --> C{"Prefix cached?"}
C -- "No - first time" --> P1["Full price for all 2000 tokens"]
R2["Request 2:\nSame system prompt + new query"] --> C2{"Prefix cached?"}
C2 -- "Yes - cache hit" --> P2["Discounted rate for cached 2000 tokens\n+ full price for new query only"]
\`\`\` 

**Above shown diagram - Prompt Caching in Practice.** The static prefix is processed once and reused, 
so only the genuinely new part of each subsequent request incurs full-price processing.

**Remove unnecessary context.** More context does not always produce better answers. Context 
should be relevant, not excessive.

---

## Controlling Output Tokens

Several simple techniques can significantly reduce unnecessary response generation.

**Ask for structured output.** Instead of requesting a lengthy explanation, ask for JSON, bullet 
points, tables, or short summaries. Structured responses are usually more concise and easier for 
downstream systems to process.

**Limit maximum output tokens.** Most APIs allow developers to define the maximum number of tokens 
the model is permitted to generate. This prevents unexpectedly large responses.

**Ask precise questions.** Broad prompts encourage lengthy responses. Specific prompts usually 
produce more focused answers. Compare these examples:

- ❌ *Explain cloud computing.*
- ✅ *Explain cloud computing in five bullet points suitable for a CIO.*

The second prompt is likely to produce a shorter, more targeted response.

> **Enterprise Perspective.** Optimizing token consumption is not about making prompts shorter. 
It is about eliminating unnecessary tokens while preserving response quality. That distinction is 
extremely important. A well-designed enterprise AI platform aims to maximize business value per 
token consumed, not simply minimize token usage.

> **From the Engineering Desk.** Token optimization is rarely a prompt engineering exercise alone. 
It is an architectural exercise. Prompt templates, conversation summarization, retrieval strategies, 
response validation, structured outputs, model routing, and caching all contribute to token efficiency. 
Viewed individually, each optimization may appear small. Viewed across millions of requests every month, 
they become significant. This is where engineering discipline begins to create measurable business value.

---

##  Model Routing: Right-Sizing the Model to the Task

Not every request needs the largest, most capable model available. A classification task, 
a short summarization, or a simple extraction job can often be handled just as accurately by a smaller, 
cheaper, faster model - while a complex multi-step reasoning task genuinely benefits from a frontier model.

This is where token economics meets architecture. **Model routing** - sometimes called a ***model cascade*** 
or ***model gateway*** - inspects each incoming request and directs it to the most cost-appropriate model 
capable of handling it.

\`\`\`mermaid

flowchart TD

A["Incoming request"] --> B{"Classify task\ncomplexity"}
B -- "Simple: classification,\nextraction, formatting" --> C["Small/fast model\n(low $/token)"]
B -- "Moderate: summarization,\nstandard Q&A" --> D["Mid-tier model"]
B -- "Complex: multi-step reasoning,\ncode generation, planning" --> E["Frontier model\n(high $/token)"]
C --> F["Response + usage metadata\nlogged for chargeback"]
D --> F
E --> F

\`\`\`

**Above shown diagram - A Model Routing Gateway.** Routed correctly, an enterprise platform pays frontier-model 
prices only for requests that genuinely need frontier-model reasoning - while the bulk of routine 
traffic runs on cheaper, faster models. This single architectural decision often has more impact on 
total token spend than any individual prompt optimization.

---

##  Token Economics: A Simple Cost Formula

At its simplest, the cost of a single LLM call is:

\`\`\`
Request Cost = ( Input Tokens  x  Input Price  ÷ 1,000,000 )
			 + ( Output Tokens x  Output Price ÷ 1,000,000 )
\`\`\` 



The moment this formula is multiplied across a real workload, small inefficiencies compound quickly:

| Technique | Tokens Before (In / Out) | Monthly Cost Before | Tokens After (In / Out) | Monthly Cost After | Savings |
|---|---|---|---|---|---|
| Summarize history instead of sending full history (2M requests/mo) | 8,000 / 400 | $9,200 | 1,200 / 400 | $2,400 | **74% lower** ($6,800/mo) |
| Use RAG instead of stuffing the full document (500K requests/mo) | 15,000 / 300 | $3,975 | 2,000 / 300 | $725 | **82% lower** ($3,250/mo) |
| Ask for structured output instead of free text (1M requests/mo) | 500 / 1,800 | $2,950 | 500 / 350 | $775 | **74% lower** ($2,175/mo) |


*Costs are illustrative,* ***model priced at $0.50 per 1M input 
tokens and $1.50 per 1M output tokens*** *- the exact rate will vary by model and provider. 
What stays constant across providers is the pattern: the biggest cost reductions come from 
architectural decisions, not from trimming a few words out of a prompt.*

The exact multipliers will vary by model and provider, but the pattern is consistent: **the biggest 
cost reductions rarely come from trimming a few words out of a prompt - they 
come from architectural decisions** like RAG instead of full-document stuffing, 
summarized history instead of full history, structured output instead of free text, 
and routing simple tasks to smaller models.

---

##  Token Observability: Treating Tokens Like Any Other SRE Metric

For teams that already run mature observability practices - dashboards for latency percentiles, error 
budgets, MTTD and MTTR - extending that discipline to token consumption is a natural next step rather.

A minimal token observability layer typically tracks:

- **Volume metrics** - input tokens, output tokens, and total tokens per request, aggregated by model, 
endpoint, and business unit.
- **Efficiency metrics** - output-to-input ratio, cache-hit rate on cached prompt prefixes, and 
tokens-per-successful-outcome (not just tokens-per-request).
- **Cost metrics** - estimated spend per request, rolled up into daily/weekly trends and compared 
against budget, enabling chargeback to the business unit generating the traffic.
- **Anomaly signals** - sudden spikes in average output tokens (often a sign of a prompt regression 
or a model producing runaway responses), or context-window-limit errors (a sign that conversation history 
or retrieved context has grown unchecked).

Wiring these into an existing metrics stack - *Prometheus counters for token volume, Grafana panels 
alongside latency and error-rate dashboards* - means token consumption gets treated the same way 
CPU or memory would be: something with baselines, thresholds, and alerts, not something discovered 
for the first time on an invoice at the end of the month.

---

##  Key Takeaways

- A token is not a word, and estimating cost or context usage by word count is unreliable - tokenization 
is subword-based and varies by language, formatting, and vocabulary.

- Input and output tokens are computationally and economically different: input is processed in parallel, 
output is generated sequentially, and pricing reflects that asymmetry.

- The context window is one shared token budget - not separate budgets for input and output. 
Every token counts. Conversation history, retrieved documents, tool definitions, and 
the model's response all compete for the same available space.

- The largest cost reductions typically come from architecture - RAG instead of full-document stuffing, 
summarization instead of full history, structured output instead of free text, and model routing instead of a 
one-size-fits-all model - not from trimming individual prompts.

- Token consumption deserves the same operational rigor as any other production metric: measured, 
dashboarded, alerted on, and reviewed - not discovered retroactively on a bill.

`},





{
    slug: 'GitGub-Tokens-Models-LLMs',
    title: 'Developer utilities: Engineering with Tokens, Models & LLMs',
    subtitle: "Building reusable engineering utilities for tokens, models, resilience and observability.",
    date: '2026-08-15',
    readingTime: 5,
    tags: ['AI', 'Tokens', 'Models', 'LLMs'],
    body: `

> Exploring practical engineering utilities for AI and LLM applications - from token economics and model usage 
to resilience, workload processing, model routing and observability.


![Cover Image](/images/Article.png)


When I joined **Deutsche Bank**, one of the things that stayed with me from our automation work was the value 
of reusable engineering components.

We developed and maintained libraries, modules, utilities and functions - we named them ***db_tools*** - around common problems so that teams didn't have to reinvent the 
wheel - standardized logging, centrally approved password-encryption utilities, file housekeeping scripts and 
other reusable automation components.

I started developing a set of utilities that I believe could be useful when building enterprise AI applications. 
My goal was to make them reusable, so that other engineers can build on them instead of reinventing the wheel.

---

## From LLM APIs to AI engineering

Calling an LLM API is relatively easy.

The engineering questions begin afterwards:

- How much did the request cost?
- How many tokens did it consume?
- What happens when the API times out?
- How should transient failures be retried?
- When should we use synchronous versus batch execution?
- Should every request use the same model?
- How should different workloads be routed?
- How do we observe the health of an AI workload?

These are familiar engineering questions - reliability, performance, cost, scalability and observability - 
applied to a new technology domain.

That became the focus of this project.

---

### The Enterprise AI Engineering Lab

This project is an open-source collection of practical Python utilities that has been developed to 
explore engineering concerns around LLM-based applications - in particular when tokens are involved.

The first milestone contains **10 completed utilities**, progressing from LLM/token fundamentals toward resilience, workload
 engineering, model routing and observability.

\`\`\`text
LLM / Token Engineering
          │
          ▼
   Engineering Metrics
          │
          ▼
 Response & Structured Output
          │
          ▼
    Retry & Resilience
          │
          ▼
Batch Processing & Benchmarking
          │
          ▼
   Dynamic Model Routing
          │
          ▼
     AI Observability
          │
          ▼
 Enterprise AI Engineering
\`\`\`

### The questions they address.

| Utility | Engineering Question |
|---|---|
| 01-06 | How can common LLM capabilities and engineering metrics be made reusable? |
| 07 - Retry & Resilience | How should transient AI failures be handled without creating retry storms? |
| 08 - Batch Processing | How do synchronous and asynchronous execution models differ? |
| 09 - Model Routing | When does dynamic model selection justify its additional overhead? |
| 10 - AI Observability | How can AI telemetry become actionable engineering signals? |

The repository contains the implementation, documentation, experiments and versioned releases.

**GitHub:**  
https://github.com/wali-veer/Enterprise-AI-Engineering-Lab

**Latest release:**  
https://github.com/wali-veer/Enterprise-AI-Engineering-Lab/releases/tag/v0.10.0

---

### A few engineering lessons
The project produced several useful observations.
#### Resilience is more than retrying
Transient failures should be retried carefully, using controlled retry limits and exponential backoff. 
Aggressive retries can amplify downstream problems.
#### Batch and individual execution are different tools
Batch processing is not simply a "faster" version of individual execution.
The appropriate choice depends on whether the workload requires immediate responses, request-level latency 
or asynchronous processing.
#### Dynamic routing has a cost
Model routing can match requests to different model capabilities, but routing itself introduces latency, 
token consumption and cost.
It is most valuable when workload characteristics justify that overhead.
#### AI observability needs more than success/failure
One of the most interesting results came from Utility 10.

An execution produced:
\`\`\`text
Success Rate    : 100.00%
Error Rate      :   0.00%
Average Latency : 25.39 sec
\`\`\`

Every request succeeded, yet the workload was classified as **CRITICAL** because latency crossed the configured threshold.

That reinforced an important principle:

> Successful requests do not necessarily mean a healthy AI application.

Reliability, latency, token consumption and cost need to be observed together.

The thresholds used in the project are demonstration values; production systems should derive them from workload-specific 
SLOs and business requirements.

---

### Building it with an AI thought partner

I also want to be transparent about how this project was developed.

**ChatGPT was part of the development process - but I don't describe this 
as "AI built the project."**

I approached the project as the **engineering thought leader**: defining the problems, challenging 
assumptions, making design decisions, reviewing outputs, questioning misleading conclusions 
and deciding what was ready to release.

ChatGPT acted as a **thought partner** - helping explore ideas, challenge alternatives, 
accelerate implementation, review outputs and improve documentation.

For me, this became an experiment in **AI-assisted engineering**:

---

### The first milestone is complete

**v0.10.0 is an initial milestone, not the final destination.**

The project now has a foundation for reusable AI engineering utilities, with implementation, documentation, regression testing and versioned GitHub releases.

Future iterations may explore deeper observability, evaluation, AI workload governance, cost engineering, reliability patterns and other enterprise AI engineering concerns.

I deliberately don't want to define all of those today. ***The next engineering question should emerge from the work itself.***

---

### Explore the project

**GitHub Repository**  
https://github.com/wali-veer/Enterprise-AI-Engineering-Lab

**v0.10.0 Release**  
https://github.com/wali-veer/Enterprise-AI-Engineering-Lab/releases/tag/v0.10.0

**Documentation**  
https://github.com/wali-veer/Enterprise-AI-Engineering-Lab/tree/main/docs

**Portfolio**  
https://veereshwali.in/
`
  }

];
